import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useScrollToAnimate} from '../../../shared';
import {easing} from 'epic-ue-theme';
import classNames from 'classnames';
import {motion, transform} from 'framer-motion';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const Styled = styled(motion.div)`
    height: 100%;
    width: 100%;
    transform-origin: top center;
`;
export interface ScaleOnViewWrapProps {
    minScale?: number;
    maxScale?: number;
    minPercentVisible?: number;
    duration?: number;
    delay?: number;
    children?: React.ReactNode;
    id: string;
    threshold?: number | number[];
    disable?: boolean;
    disableFromBottom?: boolean;
    disableFromTop?: boolean;
    onFullScaleChange?: (fullScale: boolean) => void;
}

export const ScaleOnViewWrap = ({
    children,
    minScale = 0.6,
    maxScale = 1,
    minPercentVisible = 0,
    duration = 0.5,
    delay = 0,
    id,
    threshold = 0.3,
    disable = false,
    disableFromBottom,
    disableFromTop,
    onFullScaleChange
}: ScaleOnViewWrapProps): JSX.Element => {
    const [fullScale, setFullScale] = useState(true);
    const {ref, percentVisible, elId} = useScrollToAnimate({
        id,
        type: 'scale',
        threshold,
        disableFromBottom,
        disableFromTop
    });

    useEffect(() => {
        const isFullScale = percentVisible === 1;
        if (onFullScaleChange && ((isFullScale && !fullScale) || (!isFullScale && fullScale))) {
            onFullScaleChange(isFullScale);
        }
        setFullScale(isFullScale);
    }, [percentVisible]);

    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    if (disable || reducedMotion) {
        return <>{children}</>;
    }

    let isMin = false;
    let isMax = false;
    if (percentVisible <= minPercentVisible) {
        isMin = true;
    } else if (percentVisible === 1) {
        isMax = true;
    }

    const scaleOutput = transform(percentVisible, [minPercentVisible, 1], [minScale, maxScale]);

    const animate = {
        scaleX: scaleOutput,
        scaleY: scaleOutput,
        transition: {delay, duration, easing}
    };

    const classnames = classNames('scale-motion', {min: isMin, max: isMax});

    return (
        <Styled className={classnames} ref={ref} id={elId} animate={animate} initial={false}>
            {children}
        </Styled>
    );
};
